import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { leadsApi } from "@/api/leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";

const COLORS = ["#2563eb", "#16a34a", "#dc2626", "#9333ea", "#ea580c", "#0891b2", "#ca8a04", "#db2777"];

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

export type CampaignRecommendationAction =
  | "SCALE_UP"
  | "SCALE_DOWN"
  | "HOLD"
  | "PAUSE"
  | "NOT_ENOUGH_DATA"
  | "NEED_SPEND";

export type CampaignRecommendationRow = {
  campaignKey: string;
  campaignName: string;
  platform: "GOOGLE" | "META";
  sourceKey: string;
  leadCount: number;
  interestedCount: number;
  interestedPct: number | null;
  totalSpend: number;
  dailySpend: number | null;
  cpl: number | null;
  recommendation: CampaignRecommendationAction;
  recommendationLabel: string;
  recommendationReason: string;
};

export type CampaignRecommendationReport = {
  window: {
    from: string;
    to: string;
    days: number;
    label: string;
    description: string;
  };
  campaigns: CampaignRecommendationRow[];
  medians: {
    interestedPct: number | null;
    cpl: number | null;
  };
};

const RECOMMENDATION_STYLES: Record<
  CampaignRecommendationAction,
  { badge: string; dot: string }
> = {
  SCALE_UP: { badge: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
  HOLD: { badge: "bg-amber-100 text-amber-900 border-amber-200", dot: "bg-amber-500" },
  SCALE_DOWN: { badge: "bg-orange-100 text-orange-900 border-orange-200", dot: "bg-orange-500" },
  PAUSE: { badge: "bg-red-100 text-red-800 border-red-200", dot: "bg-red-500" },
  NOT_ENOUGH_DATA: { badge: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400" },
  NEED_SPEND: { badge: "bg-sky-100 text-sky-900 border-sky-200", dot: "bg-sky-500" },
};

function fmtPct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `${v.toFixed(1)}%`;
}

function fmtMoney(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  return inr.format(v);
}

type Props = {
  hospitalId?: string;
};

export function CampaignRecommendationTab({ hospitalId }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canEditSpend = user?.role === "HOSPITAL_ADMIN" || user?.role === "SUPER_ADMIN";
  const [editingSpend, setEditingSpend] = useState<string | null>(null);

  const reportQuery = useQuery({
    queryKey: ["campaign-recommendations", hospitalId],
    queryFn: async () => {
      const res = await leadsApi.getCampaignRecommendationsReport(
        hospitalId ? { hospitalId } : undefined
      );
      return res.data.data as CampaignRecommendationReport;
    },
  });

  const spendMutation = useMutation({
    mutationFn: (payload: {
      spendDate: string;
      spendEnd?: string;
      amount: number;
      source?: string;
      campaignName?: string;
      hospitalId?: string;
    }) => leadsApi.upsertLeadAdSpend(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-recommendations"] });
      setEditingSpend(null);
      toast.success("Daily spend saved for analysis window");
    },
    onError: () => toast.error("Failed to save spend"),
  });

  const report = reportQuery.data;
  const campaigns = report?.campaigns ?? [];

  /** Campaigns with enough leads for quality % (plotted on X axis). */
  const qualityCampaigns = useMemo(
    () => campaigns.filter((c) => c.interestedPct != null && c.leadCount >= 15),
    [campaigns]
  );

  const [draftSpendByKey, setDraftSpendByKey] = useState<Record<string, number>>({});

  const effectiveDailySpend = (row: CampaignRecommendationRow): number | null => {
    const draft = draftSpendByKey[row.campaignKey];
    if (draft != null && !Number.isNaN(draft)) return draft;
    return row.dailySpend;
  };

  const effectiveCpl = (row: CampaignRecommendationRow): number | null => {
    if (!report) return row.cpl;
    const daily = effectiveDailySpend(row);
    if (daily == null || daily <= 0 || row.leadCount <= 0) return null;
    return (daily * report.window.days) / row.leadCount;
  };

  const plottableCampaigns = useMemo(
    () =>
      qualityCampaigns
        .map((c) => ({
          ...c,
          chartCpl: effectiveCpl(c),
        }))
        .filter((c) => c.chartCpl != null && c.chartCpl! > 0),
    [qualityCampaigns, draftSpendByKey, report]
  );

  const colorByKey = useMemo(() => {
    const map = new Map<string, string>();
    qualityCampaigns.forEach((c, i) => {
      map.set(c.campaignKey, COLORS[i % COLORS.length]!);
    });
    return map;
  }, [qualityCampaigns]);

  const chartYMax = useMemo(() => {
    const values = plottableCampaigns.map((c) => c.chartCpl ?? 0);
    const median = report?.medians.cpl ?? 0;
    const max = Math.max(...values, median, 500);
    return Math.ceil(max * 1.15);
  }, [plottableCampaigns, report?.medians.cpl]);

  const chartXMax = useMemo(() => {
    const values = qualityCampaigns.map((c) => c.interestedPct ?? 0);
    const median = report?.medians.interestedPct ?? 0;
    const max = Math.max(...values, median, 10);
    return Math.min(100, Math.ceil(max * 1.15));
  }, [qualityCampaigns, report?.medians.interestedPct]);

  const saveDailySpend = (row: CampaignRecommendationRow, dailyAmount: number) => {
    if (!report || dailyAmount < 0 || Number.isNaN(dailyAmount)) return;
    setDraftSpendByKey((prev) => ({ ...prev, [row.campaignKey]: dailyAmount }));
    const total = dailyAmount * report.window.days;
    spendMutation.mutate({
      spendDate: report.window.from,
      spendEnd: report.window.to,
      amount: total,
      source: row.sourceKey,
      campaignName: row.campaignName,
      hospitalId,
    });
  };

  const onSpendDraftChange = (row: CampaignRecommendationRow, raw: string) => {
    const v = parseFloat(raw);
    setDraftSpendByKey((prev) => {
      const next = { ...prev };
      if (Number.isNaN(v)) delete next[row.campaignKey];
      else next[row.campaignKey] = v;
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <Card className="border-slate-200/80">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Campaign budget recommendations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            Automatic scale / hold / pause guidance for <strong className="text-foreground">Meta</strong> and{" "}
            <strong className="text-foreground">Google</strong> paid campaigns. Lead quality uses leads created{" "}
            <strong className="text-foreground">9–3 days ago (IST)</strong> — the last 3 days are excluded so your
            team can mark Interested. A campaign needs at least <strong className="text-foreground">15 leads</strong>{" "}
            in that window before a recommendation is shown.
          </p>
          {report && (
            <p className="text-foreground/80">
              Analysis window: <span className="font-medium">{report.window.label}</span> ({report.window.days} days).{" "}
              {report.window.description}
            </p>
          )}
          {report?.medians.interestedPct != null && report.medians.cpl != null && (
            <p>
              Peer medians (campaigns with enough leads + spend): Interested{" "}
              <span className="font-medium text-foreground">{fmtPct(report.medians.interestedPct)}</span>, CPL{" "}
              <span className="font-medium text-foreground">{fmtMoney(report.medians.cpl)}</span>.
            </p>
          )}
        </CardContent>
      </Card>

      {reportQuery.isLoading && <p className="text-sm text-muted-foreground">Loading campaign recommendations…</p>}
      {reportQuery.isError && (
        <p className="text-sm text-red-600">Failed to load recommendations. Try again or pick a hospital.</p>
      )}

      {!reportQuery.isLoading && campaigns.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No Meta or Google paid leads in the analysis window for this hospital.
          </CardContent>
        </Card>
      )}

      {qualityCampaigns.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Quality vs cost (scatter)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {plottableCampaigns.length === 0 && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200/80 rounded-md px-3 py-2">
                Enter <strong>daily spend</strong> for one or more campaigns in the table below to plot them on this
                chart (X = Interested %, Y = cost per lead).
              </p>
            )}
            <div className="h-[420px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 16, right: 24, bottom: 48, left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    dataKey="interestedPct"
                    name="Interested"
                    unit="%"
                    domain={[0, chartXMax]}
                    label={{
                      value: "% leads ever marked Interested",
                      position: "insideBottom",
                      offset: -28,
                      style: { fontSize: 12 },
                    }}
                  />
                  <YAxis
                    type="number"
                    dataKey="chartCpl"
                    name="CPL"
                    domain={[0, chartYMax]}
                    tickFormatter={(v) => (v >= 1000 ? `₹${Math.round(v / 1000)}k` : `₹${Math.round(v)}`)}
                    label={{
                      value: "Cost per lead (INR)",
                      angle: -90,
                      position: "insideLeft",
                      style: { fontSize: 12 },
                    }}
                  />
                  {report?.medians.interestedPct != null && plottableCampaigns.length >= 2 && (
                    <ReferenceLine
                      x={report.medians.interestedPct}
                      stroke="#94a3b8"
                      strokeDasharray="4 4"
                      label={{ value: "Median Interested %", position: "top", fontSize: 10 }}
                    />
                  )}
                  {report?.medians.cpl != null && plottableCampaigns.length >= 2 && (
                    <ReferenceLine
                      y={report.medians.cpl}
                      stroke="#94a3b8"
                      strokeDasharray="4 4"
                      label={{ value: "Median CPL", position: "right", fontSize: 10 }}
                    />
                  )}
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      if (name === "Interested") return [`${Number(value).toFixed(1)}%`, "Interested"];
                      if (name === "CPL") return [fmtMoney(Number(value)), "CPL"];
                      return [value, name];
                    }}
                    labelFormatter={(_, payload) => {
                      const row = payload?.[0]?.payload as CampaignRecommendationRow & { chartCpl?: number };
                      if (!row) return "";
                      return `${row.platform}: ${row.campaignName}`;
                    }}
                  />
                  <Legend verticalAlign="top" wrapperStyle={{ fontSize: 11, paddingBottom: 8 }} />
                  {plottableCampaigns.map((c) => (
                    <Scatter
                      key={c.campaignKey}
                      name={`${c.platform}: ${c.campaignName}`}
                      data={[{ ...c, chartCpl: c.chartCpl }]}
                      fill={colorByKey.get(c.campaignKey)}
                    />
                  ))}
                </ScatterChart>
              </ResponsiveContainer>
            </div>
            <div className="border-t pt-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Campaign legend</p>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {qualityCampaigns.map((c) => (
                  <div key={c.campaignKey} className="flex items-center gap-1.5 text-xs max-w-[280px]">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: colorByKey.get(c.campaignKey) }}
                    />
                    <span className="truncate" title={`${c.platform}: ${c.campaignName}`}>
                      {c.platform}: {c.campaignName}
                    </span>
                    <span className="text-muted-foreground tabular-nums shrink-0">
                      ({fmtPct(c.interestedPct)}
                      {effectiveCpl(c) != null ? `, ${fmtMoney(effectiveCpl(c))}` : ", spend?"})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {campaigns.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Campaign breakdown</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm border-collapse min-w-[960px]">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="p-2 font-medium">Campaign</th>
                  <th className="p-2 font-medium text-center">Platform</th>
                  <th className="p-2 font-medium text-center">Leads</th>
                  <th className="p-2 font-medium text-center">Interested</th>
                  <th className="p-2 font-medium text-center">Interested %</th>
                  <th className="p-2 font-medium text-right">Daily spend</th>
                  <th className="p-2 font-medium text-right">Total spend</th>
                  <th className="p-2 font-medium text-right">CPL</th>
                  <th className="p-2 font-medium">Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((row) => {
                  const style = RECOMMENDATION_STYLES[row.recommendation];
                  const isEditing = editingSpend === row.campaignKey;
                  return (
                    <tr key={row.campaignKey} className="border-b last:border-0 align-top">
                      <td className="p-2 font-medium max-w-[220px]">
                        <span
                          className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
                          style={{ backgroundColor: colorByKey.get(row.campaignKey) ?? "#94a3b8" }}
                        />
                        {row.campaignName}
                      </td>
                      <td className="p-2 text-center">{row.platform}</td>
                      <td className="p-2 text-center tabular-nums">{row.leadCount}</td>
                      <td className="p-2 text-center tabular-nums">{row.interestedCount}</td>
                      <td className="p-2 text-center tabular-nums">{fmtPct(row.interestedPct)}</td>
                      <td className="p-2 text-right">
                        {canEditSpend && isEditing ? (
                          <Input
                            type="number"
                            min={0}
                            step={100}
                            className="h-8 w-28 ml-auto text-right tabular-nums"
                            defaultValue={effectiveDailySpend(row) ?? ""}
                            autoFocus
                            onChange={(e) => onSpendDraftChange(row, e.target.value)}
                            onBlur={(e) => {
                              const v = parseFloat(e.target.value);
                              if (!Number.isNaN(v)) saveDailySpend(row, v);
                              else setEditingSpend(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                const v = parseFloat((e.target as HTMLInputElement).value);
                                if (!Number.isNaN(v)) saveDailySpend(row, v);
                              }
                              if (e.key === "Escape") setEditingSpend(null);
                            }}
                          />
                        ) : canEditSpend ? (
                          <button
                            type="button"
                            className="tabular-nums text-right w-full hover:underline text-primary"
                            onClick={() => {
                              setEditingSpend(row.campaignKey);
                              if (row.dailySpend != null) {
                                setDraftSpendByKey((prev) => ({
                                  ...prev,
                                  [row.campaignKey]: row.dailySpend!,
                                }));
                              }
                            }}
                          >
                            {effectiveDailySpend(row) != null ? fmtMoney(effectiveDailySpend(row)) : "Add daily spend"}
                          </button>
                        ) : (
                          <span className="tabular-nums">{fmtMoney(effectiveDailySpend(row))}</span>
                        )}
                      </td>
                      <td className="p-2 text-right tabular-nums">
                        {fmtMoney(
                          effectiveDailySpend(row) != null && report
                            ? effectiveDailySpend(row)! * report.window.days
                            : row.totalSpend || null
                        )}
                      </td>
                      <td className="p-2 text-right tabular-nums">{fmtMoney(effectiveCpl(row))}</td>
                      <td className="p-2">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium ${style.badge}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                          {row.recommendationLabel}
                        </span>
                        <p className="text-xs text-muted-foreground mt-1 max-w-xs">{row.recommendationReason}</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {canEditSpend && report && (
              <p className="text-xs text-muted-foreground mt-3">
                Daily spend is saved for the full analysis window ({report.window.label}) and used to calculate CPL.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {campaigns.length > 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => reportQuery.refetch()} disabled={reportQuery.isFetching}>
            Refresh
          </Button>
        </div>
      )}
    </div>
  );
}
